import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  styled,
} from "@mui/material";
import CInput from "../../../../components/CInput";
import CAutocomplete from "../../../../components/CAutocomplete";
import {
  getRoomTypes,
  getRoomStyles,
  getRoomPalettes,
  createRoom,
  updateRoom,
} from "../../../../services/roomService";

const StyledButton = styled(Button)(({ theme }) => ({
  background: "#4D6BF6",
  color: "#ffffff",
  boxShadow: "none",
  border: "1px solid #4D6BF6",
  whiteSpace: "nowrap",
  fontSize: "16px",
  fontWeight: "500",
  borderRadius: "10px",
  padding: ".5vh 1vw",
  letterSpacing: "0",
  "&:hover": {
    borderColor: "#4D6BF6",
    color: "#ffffff",
    backgroundColor: "#4D6BF6",
  },
  "&:disabled": {
    opacity: 0.7,
    borderColor: "#4D6BF6",
    color: "#ffffff",
    backgroundColor: "#4D6BF6",
    cursor: "not-allowed",
  },
}));

const GreyStyledButton = styled(Button)(({ theme }) => ({
  background: "#fff",
  color: "#0D0D0E",
  boxShadow: "none",
  border: "1px solid #D7D8D9",
  whiteSpace: "nowrap",
  fontSize: "16px",
  fontWeight: "500",
  borderRadius: "10px",
  padding: ".5vh 1vw",
  letterSpacing: "0",
}));

const paletteColors = {
  "Warm greys": ["#F2F1EF", "#D8D4CF", "#B8B2AC", "#8F8A84", "#5F5A54"],
  "Neutral tones": ["#F5F5F5", "#E0E0E0", "#C2C2C2", "#9E9E9E", "#616161"],
  "Jewel tones": ["#2C3E50", "#6A1B9A", "#0B5345", "#1F618D", "#7D3C98"],
  "Coastal blue": ["#EAF6F6", "#A8DADC", "#5DADE2", "#2874A6", "#1B4F72"],
  "Forest green": ["#E8F5E9", "#A5D6A7", "#388E3C", "#1B5E20", "#0B3D0B"],
  "Earth and terracotta": ["#F4E1D2", "#D7A86E", "#C65D3A", "#9C3D2E", "#5C2A1D"],
};

const ColorPalettePreview = ({ colors }) => {
  if (!colors || colors.length === 0) return null;
  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex" }}>
      {colors.map((color, index) => (
        <Box key={index} sx={{ flex: 1, backgroundColor: color }} />
      ))}
    </Box>
  );
};

const QuickSelectionButton = styled(Button)(({ theme }) => ({
  padding: "1vh 1vw",
  border: "1px solid #e0e0e0",
  boxShadow: "none",
  borderRadius: "8px",
  textAlign: "left",
  textTransform: "none",
  width: "100%",
  backgroundColor: "#fff",
  color: "#0D0D0E",
  fontSize: "12px",
  fontWeight: "500",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  "&:hover": {
    borderColor: "#4D6BF6",
    backgroundColor: "#f3f8ff",
  },
}));

const AddRoomModal = ({ open, onClose, onAddRoom, projectDetails, editRoom }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    roomName: "",
    roomType: "",
    roomTypeId: null,
    area: "",
    height: "",
    style: "",
    styleId: null,
    palette: "",
    paletteId: null,
  });

  const [errors, setErrors] = useState({});
  const [quickSelectionRooms, setQuickSelectionRooms] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [roomStyles, setRoomStyles] = useState([]);
  const [roomPalettes, setRoomPalettes] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const placeholderImage =
    "https://renohome.blob.core.windows.net/reno-cms/e56d3d53-e335-425f-990e-16e6b2bbee1b";

  // Load data when modal opens
  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, projectDetails]);

  // Initialize form data when editing
  useEffect(() => {
    if (editRoom && open && !loadingData && roomTypes.length > 0 && roomStyles.length > 0 && roomPalettes.length > 0) {
      // Find style and palette IDs from names
      const currentStyle = roomStyles.find((rs) => rs.name === editRoom.style);
      const currentPalette = roomPalettes.find((rp) => rp.name === editRoom.palette);
      const currentRoomType = roomTypes.find((rt) => rt.type === editRoom.room_type);

      setFormData({
        roomName: editRoom.room_name || "",
        roomType: editRoom.room_type || "",
        roomTypeId: currentRoomType?.id || null,
        area: editRoom.area?.toString() || "",
        height: editRoom.height?.toString() || "",
        style: editRoom.style || "",
        palette: editRoom.palette || "",
        styleId: currentStyle?.id || null,
        paletteId: currentPalette?.id || null,
      });
      setCurrentStep(1);
    } else if (!editRoom && open && !loadingData) {
      // Reset form when opening for new room (only after data is loaded)
      resetForm();
    }
  }, [editRoom, open, loadingData, roomTypes, roomStyles, roomPalettes]);

  const loadData = async () => {
    try {
      setLoadingData(true);
      const projectType = projectDetails?.project_type || "Villa"; // Default fallback

      const [
        quickSelectionResponse,
        roomTypesResponse,
        stylesResponse,
        palettesResponse,
      ] = await Promise.all([
        getRoomTypes(true, projectType),
        getRoomTypes(false),
        getRoomStyles(),
        getRoomPalettes(),
      ]);

      if (quickSelectionResponse.success) {
        setQuickSelectionRooms(quickSelectionResponse.data || []);
      }
      if (roomTypesResponse.success) {
        setRoomTypes(roomTypesResponse.data || []);
      }
      if (stylesResponse.success) {
        setRoomStyles(stylesResponse.data || []);
      }
      if (palettesResponse.success) {
        setRoomPalettes(palettesResponse.data || []);
      }
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load room data. Please try again.");
    } finally {
      setLoadingData(false);
    }
  };

  const handleQuickSelection = (room) => {
    // Find the corresponding room type ID from the full room types list
    const roomTypeData = roomTypes.find((rt) => rt.type === room.type);

    setFormData({
      ...formData,
      roomName: room.name,
      roomType: room.type,
      roomTypeId: roomTypeData ? roomTypeData.id : room.id,
    });
  };

  const handleInputChange = (field, value) => {
    if (field === "roomType") {
      // Find the room type ID when manually selecting from dropdown
      const roomTypeData = roomTypes.find((rt) => rt.type === value);
      setFormData({
        ...formData,
        [field]: value,
        roomTypeId: roomTypeData ? roomTypeData.id : null,
      });
    } else {
      setFormData({
        ...formData,
        [field]: value,
      });
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: "",
      });
    }
  };

  const handleStyleSelection = (style) => {
    setFormData({
      ...formData,
      style: style.name,
      styleId: style.id,
    });
  };

  const handlePaletteSelection = (palette) => {
    setFormData({
      ...formData,
      palette: palette.name,
      paletteId: palette.id,
    });
  };

  const updateRoomAPI = async () => {
    try {
      setIsLoading(true);

      // Build the update payload with all form fields
      const updatePayload = {
        room_id: editRoom.room_id,
        name: formData.roomName,
        area: formData.area ? parseFloat(formData.area) : null,
        height: formData.height ? parseFloat(formData.height) : null,
      };

      // Include style_id if style is selected
      if (formData.styleId) {
        updatePayload.style_id = formData.styleId;
      }

      // Include color_palette_id if palette is selected
      if (formData.paletteId) {
        updatePayload.color_palette_id = formData.paletteId;
      }

      const requestBody = {
        rooms: [updatePayload],
      };

      const response = await updateRoom(projectDetails?.id, requestBody);

      if (response.success) {
        toast.success("Room updated successfully!");
        resetForm();
        onClose();
        if (onAddRoom) {
          onAddRoom();
        }
      } else {
        toast.error(response.message || "Failed to update room");
      }
    } catch (error) {
      console.error("Error updating room:", error);
      toast.error("Failed to update room. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const createRoomAPI = async () => {
    try {
      setIsLoading(true);

      // Use the stored roomTypeId or find it if not set
      let roomConfigId = formData.roomTypeId;
      if (!roomConfigId) {
        const selectedRoomType = roomTypes.find(
          (rt) => rt.type === formData.roomType
        );
        if (!selectedRoomType) {
          toast.error("Selected room type not found");
          return;
        }
        roomConfigId = selectedRoomType.id;
      }

      // Find style_id from room styles
      const selectedStyle = roomStyles.find((rs) => rs.name === formData.style);
      if (!selectedStyle) {
        toast.error("Selected style not found");
        return;
      }

      // Find color_palette_id from room palettes
      const selectedPalette = roomPalettes.find(
        (rp) => rp.name === formData.palette
      );
      if (!selectedPalette) {
        toast.error("Selected color palette not found");
        return;
      }

      const requestBody = {
        name: formData.roomName,
        area: parseFloat(formData.area) || 0,
        height: parseFloat(formData.height) || 0,
        room_config_id: roomConfigId,
        style_id: selectedStyle.id,
        color_palette_id: selectedPalette.id,
      };
      console.log("prr", projectDetails);

      const response = await createRoom(projectDetails?.id, requestBody);

      if (response.success) {
        toast.success("Room created successfully!");
        resetForm();
        onClose();
        if (onAddRoom) {
          onAddRoom();
        }
      } else {
        toast.error(response.message || "Failed to add room");
      }
    } catch (error) {
      console.error("Error creating room:", error);
      toast.error("Failed to add room. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const isNextDisabled =
    currentStep === 1
      ? !formData.roomName || !formData.roomType
      : currentStep === 2
        ? !formData.style
        : !formData.palette;

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    if (editRoom) {
      updateRoomAPI();
    } else {
      createRoomAPI();
    }
  };

  const resetForm = () => {
    setFormData({
      roomName: "",
      roomType: "",
      roomTypeId: null,
      area: "",
      height: "",
      style: "",
      styleId: null,
      palette: "",
      paletteId: null,
    });
    setCurrentStep(1);
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          padding: "8px",
          maxHeight: "90vh",
          overflowX: "clip",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          fontSize: "24px",
          fontWeight: 600,
          color: "#0D0D0E",
          paddingBottom: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <div style={{ fontSize: "16px", fontWeight: 600, letterSpacing: 0 }}>
            {editRoom ? "Edit Room - Details" : "Add New Room - Details"}
          </div>
          {/* Progress Indicator */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
            {[1, 2, 3].map((step) => (
              <Box
                key={step}
                sx={{
                  height: "4px",
                  borderRadius: "2px",
                  backgroundColor:
                    step === currentStep
                      ? "#4d6bf6"
                      : step < currentStep
                        ? "#3457f388"
                        : "#e0e0e0",
                  width:
                    step === currentStep
                      ? "24px"
                      : step < currentStep
                        ? "24px"
                        : "16px",
                }}
              />
            ))}
          </Box>
        </Box>
      </DialogTitle>

      {loadingData ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <div style={{ padding: "0 1.5vw" }}>
          {currentStep === 1 && (
            <div sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Quick Selection - Only show when not editing */}
              {!editRoom && (
                <div>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: "16px",
                      fontWeight: 500,
                      mb: 1,
                      letterSpacing: 0,
                    }}
                  >
                    Quick Selection
                  </Typography>
                  <Box sx={{ width: "100%" }}>
                    <Box
                      sx={{
                        display: "grid",
                        gap: 2,
                        gridTemplateColumns: {
                          xs: "repeat(2, 1fr)",
                          sm: "repeat(3, 1fr)",
                          md: "repeat(4, 1fr)",
                        },
                      }}
                    >
                      {quickSelectionRooms.map((room) => (
                        <div key={room.id}>
                          <QuickSelectionButton
                            onClick={() => handleQuickSelection(room)}
                          >
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 500, display: "block" }}
                            >
                              {room.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#666" }}>
                              {room.type}
                            </Typography>
                          </QuickSelectionButton>
                        </div>
                      ))}
                    </Box>
                  </Box>
                </div>
              )}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: "12px",
                  marginTop: "2vh",
                }}
              >
                <div>
                  <CInput
                    label="Room Name"
                    placeholder="e.g. Master Bedroom"
                    value={formData.roomName}
                    onChange={(e) =>
                      handleInputChange("roomName", e.target.value)
                    }
                    error={!!errors.roomName}
                    helpertext={errors.roomName}
                  />
                </div>

                <div>
                  <CAutocomplete
                    label="Room Type"
                    placeholder="Select room type"
                    value={roomTypes.find(
                      (option) => option.type === formData.roomType
                    )}
                    onChange={(e, newValue) => {
                      handleInputChange("roomType", newValue?.type || "");
                    }}
                    options={roomTypes}
                    getOptionLabel={(option) => option.type}
                    error={!!errors.roomType}
                    helpertext={errors.roomType}
                  />
                </div>

                <div>
                  <CInput
                    label="Area (m²)"
                    type="number"
                    placeholder="e.g. 25"
                    value={formData.area}
                    onChange={(e) => handleInputChange("area", e.target.value)}
                    error={!!errors.area}
                    helpertext={errors.area}
                  />
                </div>

                <div>
                  <CInput
                    label="Height (m)"
                    type="number"
                    placeholder="e.g. 3.2"
                    value={formData.height}
                    onChange={(e) =>
                      handleInputChange("height", e.target.value)
                    }
                    error={!!errors.height}
                    helpertext={errors.height}
                  />
                </div>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  fontSize: "16px",
                  fontWeight: 500,
                  mb: 0,
                  letterSpacing: 0,
                }}
              >
                Choose Your Style
              </Typography>
              <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
                Choose the style that best represents the feeling you want to
                create in this space.
              </Typography>

              {/* Style Selection Grid */}
              <div
                style={{
                  display: "grid",
                  gap: "24px",
                  gridTemplateColumns: "repeat(3, 1fr)",
                }}
              >
                {roomStyles?.map((style) => (
                  <div key={style.id}>
                    <Button
                      onClick={() => handleStyleSelection(style)}
                      sx={{
                        position: "relative",
                        border:
                          formData.style === style.name
                            ? "1px solid #4d6bf6"
                            : "1px solid #e0e0e0",
                        borderRadius: "8px",
                        textAlign: "left",
                        textTransform: "none",
                        width: "100%",
                        padding: 0,
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        boxShadow: "none",
                        "&:hover": {
                          border:
                            formData.style === style.name
                              ? "1px solid #4d6bf6"
                              : "1px solid #e0e0e0",
                        },
                      }}
                    >
                      {formData.style === style.name && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            width: "20px",
                            height: "20px",
                            backgroundColor: "#4d6bf6",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 10,
                          }}
                        >
                          <Typography sx={{ color: "white", fontSize: "12px" }}>
                            ✓
                          </Typography>
                        </Box>
                      )}

                      {/* Style Image */}
                      <Box
                        sx={{
                          width: "100%",
                          height: "112px",
                          backgroundColor: "#f5f5f5",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <img
                          src={
                            style.image_url && style.image_url.trim() !== ""
                              ? style.image_url
                              : placeholderImage
                          }
                          alt={style.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                          onError={(e) => {
                            e.target.src = placeholderImage;
                          }}
                        />
                      </Box>

                      {/* Style Name */}
                      <div
                        style={{
                          fontWeight: 500,
                          fontSize: "14px",
                          padding: "1vh 0vw",
                          color: "#0D0D0E",
                        }}
                      >
                        {style.name}
                      </div>
                    </Button>
                  </div>
                ))}
              </div>
            </Box>
          )}

          {currentStep === 3 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  fontSize: "16px",
                  fontWeight: 500,
                  mb: 0,
                  letterSpacing: 0,
                }}
              >
                Select Color Palette
              </Typography>
              <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
                Select the color palette that complements your chosen style and
                personal preference.
              </Typography>

              <div
                style={{
                  display: "grid",
                  gap: "24px",
                  gridTemplateColumns: "repeat(3, 1fr)",
                }}
              >
                {roomPalettes?.map((palette) => (
                  <div key={palette.id}>
                    <Button
                      onClick={() => handlePaletteSelection(palette)}
                      sx={{
                        position: "relative",
                        border:
                          formData.palette === palette.name
                            ? "1px solid #4d6bf6"
                            : "1px solid #e0e0e0",
                        borderRadius: "8px",
                        textAlign: "left",
                        textTransform: "none",
                        width: "100%",
                        padding: 0,
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        boxShadow: "none",
                        "&:hover": {
                          border:
                            formData.style === palette.name
                              ? "1px solid #4d6bf6"
                              : "1px solid #e0e0e0",
                        },
                      }}
                    >
                      {formData.palette === palette.name && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            width: "20px",
                            height: "20px",
                            backgroundColor: "#4d6bf6",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 10,
                          }}
                        >
                          <Typography sx={{ color: "white", fontSize: "12px" }}>
                            ✓
                          </Typography>
                        </Box>
                      )}

                      {/* Palette Image */}
                      <Box
                        sx={{
                          width: "100%",
                          height: "112px",
                          backgroundColor: "#f5f5f5",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                        }}
                      >
                        {palette.image_url &&
                        palette.image_url.trim() !== "" ? (
                          <img
                            src={palette.image_url}
                            alt={palette.name}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              const fallback = paletteColors[palette.name];
                              if (fallback) {
                                e.target.style.display = "none";
                              } else {
                                e.target.src = placeholderImage;
                              }
                            }}
                          />
                        ) : paletteColors[palette.name] ? (
                          <ColorPalettePreview
                            colors={paletteColors[palette.name]}
                          />
                        ) : (
                          <img
                            src={placeholderImage}
                            alt={palette.name}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        )}
                      </Box>

                      {/* Palette Name */}
                      <div
                        style={{
                          fontWeight: 500,
                          fontSize: "14px",
                          padding: "1vh 0vw",
                          color: "#0D0D0E",
                        }}
                      >
                        {palette.name}
                      </div>
                    </Button>
                  </div>
                ))}
              </div>
            </Box>
          )}
        </div>
      )}

      <DialogActions
        sx={{ padding: "16px 24px", justifyContent: "space-between" }}
      >
        <GreyStyledButton
          onClick={currentStep === 1 ? handleClose : handlePrevious}
        >
          {currentStep === 1 ? "Close" : "Back"}
        </GreyStyledButton>

        <StyledButton
          onClick={currentStep === 3 ? handleSubmit : handleNext}
          disabled={isNextDisabled || isLoading}
        >
          {isLoading ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              {editRoom ? "Updating..." : "Creating..."}
            </>
          ) : (
            <>{currentStep === 3 ? (editRoom ? "Update Room" : "Add Room") : "Next"}</>
          )}
        </StyledButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddRoomModal;
